import axios from './Axios';
import { Product as ProductType } from 'ai-api/types/purchase';

// Email response interface
interface EmailResponse {
    code: number;
    message?: string;
    data?: any;
}

/**
 * Service for admin-related operations like sending emails
 */
class AdminService {
    /**
     * Formats purchase data as HTML for email notification
     */
    private formatPurchaseEmailContent(
        product: ProductType,
        teacherName: string | null,
        selectedDate: Date | null,
        selectedTime: Date | null,
        userEmail: string | undefined,
        userName: string | undefined
    ): string {
        // Create HTML table-based email content
        let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>New Product Purchase Notification</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    line-height: 1.6;
                    color: #333;
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }
                h1 {
                    color: #4c174d;
                    text-align: center;
                    border-bottom: 2px solid #eb90dc;
                    padding-bottom: 10px;
                }
                h2 {
                    color: #eb90dc;
                    border-bottom: 1px solid #eb90dc;
                    padding-bottom: 5px;
                    margin-top: 30px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 20px;
                }
                th {
                    background-color: #fdf9fe;
                    color: #4c174d;
                    font-weight: bold;
                    text-align: left;
                    padding: 8px;
                    border: 1px solid #eb90dc;
                }
                td {
                    padding: 8px;
                    border: 1px solid #eb90dc;
                }
                .not-provided {
                    color: #999;
                    font-style: italic;
                }
            </style>
        </head>
        <body>
            <h1>New Product Purchase</h1>
        `;

        // Purchase Information section
        htmlContent += `
            <h2>Purchase Information</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Product ID</td>
                    <td>${product.productid || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Product Name</td>
                    <td>${product.name || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Product Price</td>
                    <td>${product.finalAmount} USD</td>
                </tr>
                <tr>
                    <td>Purchase Date</td>
                    <td>${new Date().toLocaleString()}</td>
                </tr>
            </table>
        `;

        // Customer Information section
        htmlContent += `
            <h2>Customer Information</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Name</td>
                    <td>${userName || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Email</td>
                    <td>${userEmail || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Session Information section
        htmlContent += `
            <h2>Session Information</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Teacher</td>
                    <td>${teacherName || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Session Date</td>
                    <td>${selectedDate ? selectedDate.toLocaleDateString() : '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Session Time</td>
                    <td>${selectedTime ? selectedTime.toLocaleTimeString() : '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Close HTML document
        htmlContent += `
        </body>
        </html>
        `;

        return htmlContent;
    }

    /**
     * Sends purchase notification email to admin
     */
    async sendPurchaseEmail(
        product: ProductType,
        teacherName: string | null,
        selectedDate: Date | null,
        selectedTime: Date | null,
        userEmail: string | undefined,
        userName: string | undefined
    ): Promise<{ success: boolean; error?: string }> {
        try {
            const htmlContent = this.formatPurchaseEmailContent(
                product,
                teacherName,
                selectedDate,
                selectedTime,
                userEmail,
                userName
            );
            
            // Customer name or "Customer" if name not available
            const customerName = userName || 'Customer';
            
            const response = await axios.post<EmailResponse>('/admin/trigger-email', {
                email: 'mate@koreanmate.app', // Recipient email
                subject: `[New Purchase] ${customerName}: ${product.name || 'Product'}`,
                body: htmlContent,
                groups: 'mate@koreanmate.app' // Sender email - must be domain allowed in the API
            });
            
            if (response.data.code === 200) {
                return { success: true };
            } else {
                return { 
                    success: false, 
                    error: response.data.message || 'Failed to send email notification.'
                };
            }
        } catch (error) {
            console.error('Error sending purchase email:', error);
            return { 
                success: false, 
                error: 'Network error when sending notification.'
            };
        }
    }
}

const adminService = new AdminService();
export default adminService;